/**
 * Mock Engagement Data Service
 * 
 * Generates realistic but fake engagement analytics for testing.
 * This simulates data that would come from DynamoDB in production.
 */

const seededRandom = (seed) => {
  const x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
};

const getVisualTypeFactor = (visualType) => {
  const factors = {
    'visual': 1.0,
    '3d_model': 1.2,
    'molecular': 1.3,
    'organ_segmentation': 1.4,
    'presentation': 1.1,
    'scrollytelling': 1.5,
    'explainer': 1.6,
    'video': 1.4,
    'tour': 1.6
  };
  return factors[visualType] || 1.0;
};

export const generateMockEngagementForVisual = (visual) => {
  const seed = visual.id ? parseInt(visual.id.toString().replace(/\D/g, '').slice(0, 8) || '12345') : 12345;
  const typeFactor = getVisualTypeFactor(visual.type);
  
  const baseViews = Math.floor(seededRandom(seed) * 500 + 100);
  const views = Math.floor(baseViews * typeFactor);
  const likes = Math.floor(views * (0.15 + seededRandom(seed + 1) * 0.25));
  const comments = Math.floor(views * (0.02 + seededRandom(seed + 2) * 0.05));
  const saves = Math.floor(views * (0.08 + seededRandom(seed + 3) * 0.12));
  const avgTimeSeconds = Math.floor(typeFactor * (120 + seededRandom(seed + 4) * 180));
  const interactionRate = Math.floor((45 + seededRandom(seed + 5) * 30) * typeFactor);
  
  const minutes = Math.floor(avgTimeSeconds / 60);
  const seconds = avgTimeSeconds % 60;
  const avgTimeFormatted = `${minutes}:${seconds.toString().padStart(2, '0')}`;

  return {
    visualId: visual.id,
    views,
    likes,
    comments,
    saves,
    avgTime: avgTimeFormatted,
    avgTimeSeconds,
    interactionRate: Math.min(interactionRate, 95),
    trend: Math.floor(seededRandom(seed + 6) * 60) - 20,
    createdAt: visual.created_at || new Date().toISOString()
  };
};

export const generateMockEngagementData = (visuals, timeRange = '30d') => {
  const now = new Date();
  const daysMap = {
    '7d': 7,
    '30d': 30,
    '90d': 90,
    '1y': 365
  };
  const days = daysMap[timeRange] || 30;

  const visualEngagements = {};
  let totalViews = 0;
  let totalLikes = 0;
  let totalComments = 0;
  let totalSaves = 0;
  let totalTimeSeconds = 0;

  visuals.forEach(visual => {
    const engagement = generateMockEngagementForVisual(visual);
    visualEngagements[visual.id] = engagement;
    totalViews += engagement.views;
    totalLikes += engagement.likes;
    totalComments += engagement.comments;
    totalSaves += engagement.saves;
    totalTimeSeconds += engagement.avgTimeSeconds * engagement.views;
  });

  const avgTotalTimeSeconds = visuals.length > 0 ? Math.floor(totalTimeSeconds / totalViews) : 0;
  const avgTotalMinutes = Math.floor(avgTotalTimeSeconds / 60);
  const avgTotalSeconds = avgTotalTimeSeconds % 60;

  const weeklyData = Array.from({ length: Math.min(days / 7, 12) }, (_, i) => {
    const weekAgo = Math.min(days / 7, 12) - i;
    const seed = weekAgo * 100;
    return {
      week: `Week ${i + 1}`,
      visitors: Math.floor(totalViews / 10 + seededRandom(seed) * totalViews / 20),
      repeatVisitors: Math.floor(totalViews / 25 + seededRandom(seed + 1) * totalViews / 50),
      avgTimeMinutes: 5 + seededRandom(seed + 2) * 8,
      interactionRate: 60 + seededRandom(seed + 3) * 25
    };
  });

  const audienceSegments = [
    { 
      role: 'Professionals', 
      count: Math.floor(totalViews * 0.25), 
      avgTime: '12:30',
      topInterests: ['Technical details', 'Research', 'Latest updates'],
      readingStyle: 'Comprehensive',
      engagement: 'Deep explainer'
    },
    { 
      role: 'Students', 
      count: Math.floor(totalViews * 0.35), 
      avgTime: '8:15',
      topInterests: ['Visual learning', 'Basics', 'Interactive content'],
      readingStyle: 'Visual-first',
      engagement: 'Visual-first'
    },
    { 
      role: 'Enthusiasts', 
      count: Math.floor(totalViews * 0.20), 
      avgTime: '10:45',
      topInterests: ['In-depth exploration', 'Advanced topics', 'Discussions'],
      readingStyle: 'Technical',
      engagement: 'Deep explainer'
    },
    { 
      role: 'General public', 
      count: Math.floor(totalViews * 0.20), 
      avgTime: '5:20',
      topInterests: ['Quick overview', 'Visual content', 'Highlights'],
      readingStyle: 'Quick answers',
      engagement: 'Quick answers'
    }
  ];

  return {
    overview: {
      totalVisitors: totalViews,
      repeatVisitors: Math.floor(totalViews * 0.35),
      avgTimeEngaged: `${avgTotalMinutes}:${avgTotalSeconds.toString().padStart(2, '0')}`,
      contentViewsPerSession: visuals.length > 0 ? (2.1 + Math.random() * 1.5).toFixed(1) : '0',
      interactionRate: visuals.length > 0 ? Math.floor(60 + Math.random() * 25) : 0,
      topContent: visuals.length > 0 ? visuals[0].title : 'N/A',
      weeklyData
    },
    visualEngagements,
    audienceSegments,
    trending: Object.entries(visualEngagements)
      .sort((a, b) => b[1].trend - a[1].trend)
      .slice(0, 10)
      .map(([visualId, data]) => ({
        visualId,
        ...data
      }))
  };
};

export const getTrendingContent = (visuals, engagementData) => {
  if (!engagementData || !engagementData.visualEngagements) return [];
  
  return visuals
    .map(visual => ({
      visual,
      engagement: engagementData.visualEngagements[visual.id]
    }))
    .filter(item => item.engagement)
    .sort((a, b) => b.engagement.views - a.engagement.views)
    .slice(0, 10);
};

export const getContentTypeSuggestions = (visuals, channelConfig) => {
  const selectedVisuals = visuals.filter(v => 
    channelConfig.selectedVisuals.includes(v.id)
  );

  const typeCounts = {
    'visual': 0,
    'presentation': 0,
    'scrollytelling': 0,
    'explainer': 0,
    'video': 0,
    'tour': 0
  };

  selectedVisuals.forEach(visual => {
    const type = visual.type || 'visual';
    if (typeCounts.hasOwnProperty(type)) {
      typeCounts[type]++;
    } else {
      typeCounts['visual']++;
    }
  });

  const suggestions = [];

  // TODO: When backend is ready, use AI to analyze mission statement + channel content + analytics
  // For now, provide basic rule-based suggestions
  
  if (channelConfig.missionStatement && channelConfig.missionStatement.length > 50) {
    suggestions.push({
      type: 'mission_aligned',
      reason: 'AI-powered suggestions based on your mission statement will be available soon',
      priority: 'medium',
      note: 'Your mission statement has been saved and will be used to generate personalized content recommendations',
      availableCount: 0
    });
  } else if (!channelConfig.missionStatement || channelConfig.missionStatement.length === 0) {
    suggestions.push({
      type: 'add_mission',
      reason: 'Add your mission statement in Settings to get AI-powered content suggestions',
      priority: 'high',
      availableCount: 0
    });
  }

  if (typeCounts.scrollytelling === 0 && visuals.some(v => v.type === 'scrollytelling')) {
    suggestions.push({
      type: 'scrollytelling',
      reason: 'Add scrollytelling to diversify your content format',
      priority: 'high',
      availableCount: visuals.filter(v => v.type === 'scrollytelling' && !channelConfig.selectedVisuals.includes(v.id)).length
    });
  }

  if (typeCounts.explainer === 0 && visuals.some(v => v.type === 'explainer' || v.type === 'tour')) {
    suggestions.push({
      type: 'explainer',
      reason: 'Explainer videos typically have high engagement rates',
      priority: 'high',
      availableCount: visuals.filter(v => (v.type === 'explainer' || v.type === 'tour') && !channelConfig.selectedVisuals.includes(v.id)).length
    });
  }

  if (typeCounts.presentation === 0 && visuals.some(v => v.type === 'presentation' || v.type === 'slideshow')) {
    suggestions.push({
      type: 'presentation',
      reason: 'Presentations work well for structured educational content',
      priority: 'medium',
      availableCount: visuals.filter(v => (v.type === 'presentation' || v.type === 'slideshow') && !channelConfig.selectedVisuals.includes(v.id)).length
    });
  }

  const recentUploads = visuals
    .filter(v => !channelConfig.selectedVisuals.includes(v.id))
    .sort((a, b) => {
      const dateA = new Date(a.created_at || 0);
      const dateB = new Date(b.created_at || 0);
      return dateB - dateA;
    })
    .slice(0, 5);

  if (recentUploads.length > 0) {
    suggestions.push({
      type: 'recent',
      reason: `You have ${recentUploads.length} recent uploads not yet added to your channel`,
      priority: 'medium',
      visuals: recentUploads
    });
  }

  if (selectedVisuals.length < 3) {
    suggestions.push({
      type: 'minimum_content',
      reason: 'Add at least 3-5 pieces of content for a compelling channel',
      priority: 'high',
      availableCount: visuals.length - selectedVisuals.length
    });
  }

  return suggestions;
};

export const exportChannelConfig = (config) => {
  const dataStr = JSON.stringify(config, null, 2);
  const dataBlob = new Blob([dataStr], { type: 'application/json' });
  const url = URL.createObjectURL(dataBlob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `channel-config-${new Date().toISOString().split('T')[0]}.json`;
  link.click();
  URL.revokeObjectURL(url);
};
